import type {
  AudioBus,
  LoopHandle,
  LoopSpec,
  SfxRecipe as TypedSfxRecipe,
  SynthOpts,
} from '@arcraz/arcadia-types';

/**
 * Web Audio implementation of the cabinet's AudioBus.
 *
 * MVP scope (sc-2858):
 *   - synth(opts) — one-shot oscillator with attack/decay envelope
 *   - sfx(name)   — plays a registered recipe (sequence of synth notes)
 *   - bgm/duck    — no-op for v1; followup stories track real impl
 *
 * The AudioContext is lazy: browsers block AudioContext creation unless it
 * happens during a user gesture (the first keydown / pointer event). We
 * create it on-demand from the first audio call and route everything
 * through a master gain so the cabinet can mute globally.
 *
 * Mute state lives in localStorage so the player's preference survives
 * cartridge reloads.
 */

const MUTE_STORAGE_KEY = 'arcadia84:audio:muted';
const MASTER_GAIN = 0.35; // mix-friendly default

/**
 * Cabinet-controllable AudioBus. The `setMuted` / `getMuted` methods are
 * additions beyond the GameContext-facing AudioBus contract — they're
 * read by the pause overlay's mute toggle.
 */
export interface CabinetAudioBus extends AudioBus {
  setMuted(muted: boolean): void;
  getMuted(): boolean;
  /**
   * Register a named SFX recipe. Cartridges call this during preload via
   * the PreloadContext.registerSfx forwarder.
   */
  registerSfx(name: string, recipe: TypedSfxRecipe): void;
  /**
   * Start a continuous voice. Returns a handle the cartridge holds while
   * the sound is alive (engine drone, sustained tone). The cartridge
   * modulates frequency + gain live; stop() releases the voice.
   *
   * Unlike sfx() this is NOT a recipe — the cartridge owns the live
   * parameters. Use this for any sound longer than ~1 second that needs
   * to respond to game state.
   */
  loop(spec: LoopSpec): LoopHandle;
  /** Release the underlying AudioContext. Call on cabinet teardown. */
  destroy(): void;
}


export function createAudioBus(): CabinetAudioBus {
  let ctx: AudioContext | null = null;
  let masterGain: GainNode | null = null;
  let muted = readMutedFromStorage();
  const recipes = new Map<string, TypedSfxRecipe>();

  // Browsers create AudioContext in `suspended` state until the page sees a
  // user gesture. We install one-shot keydown + pointerdown listeners that
  // resume the context the first time the player interacts. Without this,
  // synth/sfx calls fire silently because the context never advances time.
  function installUnlockListeners(): void {
    if (typeof window === 'undefined') return;
    const unlock = (): void => {
      if (ctx && ctx.state === 'suspended') {
        void ctx.resume().catch(() => {
          /* best-effort */
        });
      }
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
    window.addEventListener('keydown', unlock);
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('touchstart', unlock);
  }
  installUnlockListeners();

  /**
   * Resolve a context lazily. Returns null when audio is unavailable —
   * called from inside synth/sfx so silent failure is fine.
   */
  function ensureContext(): AudioContext | null {
    if (ctx) {
      return ctx;
    }
    if (typeof window === 'undefined') {
      return null;
    }
    type AudioCtor = typeof AudioContext;
    const Ctor =
      (window.AudioContext as AudioCtor | undefined) ??
      ((window as unknown as { webkitAudioContext?: AudioCtor })
        .webkitAudioContext as AudioCtor | undefined);
    if (!Ctor) {
      return null;
    }
    try {
      ctx = new Ctor();
    } catch (err) {
      console.warn('[audio] AudioContext construction failed:', err);
      return null;
    }
    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : MASTER_GAIN;
    masterGain.connect(ctx.destination);
    console.info(
      `[audio] AudioContext created. state=${ctx.state} sampleRate=${ctx.sampleRate} muted=${muted}`
    );
    // If a user gesture already happened (e.g. selector navigation before
    // the bus existed), resume immediately. The unlock listeners catch
    // the future-gesture case.
    if (ctx.state === 'suspended') {
      void ctx.resume()
        .then(() => console.info(`[audio] resume() ok, state=${ctx?.state}`))
        .catch((err) => console.warn('[audio] resume() failed:', err));
    }
    return ctx;
  }

  /**
   * Schedule a single note at `whenSec` (an AudioContext time). Returns
   * silently if the context can't be created or if the note params are
   * out of the audible range.
   */
  function scheduleNote(opts: SynthOpts, whenSec: number): void {
    const c = ensureContext();
    if (!c || !masterGain) return;
    if (opts.f <= 0 || opts.ms <= 0) return;

    const durSec = opts.ms / 1000;
    const gain = c.createGain();
    const peak = (opts.gain ?? 0.7) * 0.6; // -4dB headroom

    // Attack/decay envelope. Short attack to avoid clicks; the rest is
    // exponential decay to silence so notes feel chip-y.
    const attackSec = Math.min(0.005, durSec * 0.1);
    gain.gain.setValueAtTime(0, whenSec);
    gain.gain.linearRampToValueAtTime(peak, whenSec + attackSec);
    gain.gain.exponentialRampToValueAtTime(0.0001, whenSec + durSec);
    gain.connect(masterGain);

    playWave(opts.wave, opts.f, durSec, gain, whenSec, c);
  }

  /**
   * Play a recipe — a single voice with pitch + gain envelopes over time.
   * Optional FM modulator adds chip-style punch. All times relative to
   * `whenSec` (an AudioContext time).
   */
  function playRecipe(recipe: TypedSfxRecipe, whenSec: number): void {
    const c = ensureContext();
    if (!c || !masterGain) return;
    if (recipe.pitch.length === 0 || recipe.gain.length === 0) return;

    // Recipe duration = max breakpoint time across both envelopes.
    let endMs = 0;
    for (const p of recipe.pitch) if (p.t > endMs) endMs = p.t;
    for (const g of recipe.gain) if (g.t > endMs) endMs = g.t;
    if (endMs <= 0) return;
    const durSec = endMs / 1000;

    const ampGain = c.createGain();
    // Gain envelope.
    ampGain.gain.setValueAtTime(0, whenSec);
    for (const g of recipe.gain) {
      const t = whenSec + g.t / 1000;
      // Cap to avoid setting exponential to 0 (illegal).
      const v = Math.max(0.0001, g.g);
      ampGain.gain.linearRampToValueAtTime(v, t);
    }
    ampGain.gain.exponentialRampToValueAtTime(0.0001, whenSec + durSec + 0.005);
    ampGain.connect(masterGain);

    if (recipe.wave === 'noise') {
      // Noise voice: schedule a noise buffer for the duration; pitch
      // envelope is ignored (noise has no fundamental).
      const buf = c.createBuffer(
        1,
        Math.max(1, Math.floor(c.sampleRate * durSec)),
        c.sampleRate
      );
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      const src = c.createBufferSource();
      src.buffer = buf;
      src.connect(ampGain);
      src.start(whenSec);
      src.stop(whenSec + durSec);
      return;
    }

    const carrier = c.createOscillator();
    carrier.type = recipe.wave;
    // Pitch envelope.
    const firstPitch = recipe.pitch[0]!;
    carrier.frequency.setValueAtTime(firstPitch.f, whenSec);
    for (const p of recipe.pitch) {
      const t = whenSec + p.t / 1000;
      carrier.frequency.linearRampToValueAtTime(Math.max(1, p.f), t);
    }
    carrier.connect(ampGain);

    if (recipe.fm) {
      // FM modulator: oscillator → gain → carrier.frequency. ratio sets
      // modulator's frequency relative to carrier's starting pitch; index
      // sets modulation depth in Hz.
      const modFreq = firstPitch.f * recipe.fm.ratio;
      const mod = c.createOscillator();
      mod.frequency.setValueAtTime(modFreq, whenSec);
      const modGain = c.createGain();
      modGain.gain.setValueAtTime(recipe.fm.index, whenSec);
      mod.connect(modGain);
      modGain.connect(carrier.frequency);
      mod.start(whenSec);
      mod.stop(whenSec + durSec);
    }

    carrier.start(whenSec);
    carrier.stop(whenSec + durSec);
  }

  function playWave(
    wave: SynthOpts['wave'],
    f: number,
    durSec: number,
    gain: GainNode,
    whenSec: number,
    c: AudioContext
  ): void {
    if (wave === 'noise') {
      // Noise = a short buffer of random samples. Cheap, doesn't pool —
      // crashes happen rarely.
      const buf = c.createBuffer(1, Math.max(1, Math.floor(c.sampleRate * durSec)), c.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < data.length; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      const src = c.createBufferSource();
      src.buffer = buf;
      src.connect(gain);
      src.start(whenSec);
      src.stop(whenSec + durSec);
    } else {
      const osc = c.createOscillator();
      osc.type = wave;
      osc.frequency.setValueAtTime(f, whenSec);
      osc.connect(gain);
      osc.start(whenSec);
      osc.stop(whenSec + durSec);
    }
  }

  return {
    synth(opts: SynthOpts): void {
      const c = ensureContext();
      if (!c) return;
      scheduleNote(opts, c.currentTime);
    },

    sfx(name: string): void {
      const recipe = recipes.get(name);
      if (!recipe) {
        console.warn(`[audio] sfx('${name}') called but no recipe registered`);
        return;
      }
      const c = ensureContext();
      if (!c) return;
      if (c.state !== 'running') {
        console.warn(
          `[audio] sfx('${name}') called but context state=${c.state} — audio will be silent until a user gesture resumes the context`
        );
      }
      playRecipe(recipe, c.currentTime);
    },

    bgm(): void {
      /* v1: stub. Looped sequencer is a separate story. */
    },
    stopBgm(): void {
      /* v1: stub */
    },
    duck(): void {
      /* v1: stub */
    },

    setMuted(next: boolean): void {
      muted = next;
      writeMutedToStorage(next);
      if (masterGain && ctx) {
        masterGain.gain.setTargetAtTime(next ? 0 : MASTER_GAIN, ctx.currentTime, 0.02);
      }
    },
    getMuted(): boolean {
      return muted;
    },
    registerSfx(name: string, recipe: TypedSfxRecipe): void {
      recipes.set(name, recipe);
    },

    loop(spec: LoopSpec): LoopHandle {
      const c = ensureContext();
      if (!c || !masterGain) {
        // Audio unavailable — return a no-op handle so callers don't
        // need to null-check. They can still call setFrequency/stop().
        return { setFrequency: () => {}, setGain: () => {}, stop: () => {} };
      }
      const now = c.currentTime;

      // Voice graph:  modulator -> modGain -> carrier.frequency
      //               carrier   -> voiceGain -> masterGain
      const carrier = c.createOscillator();
      carrier.type = spec.wave;
      carrier.frequency.setValueAtTime(spec.f, now);
      if (spec.detune) carrier.detune.setValueAtTime(spec.detune, now);

      const voiceGain = c.createGain();
      // Brief attack so the loop doesn't click on start.
      voiceGain.gain.setValueAtTime(0, now);
      voiceGain.gain.linearRampToValueAtTime(spec.gain, now + 0.02);

      carrier.connect(voiceGain);
      voiceGain.connect(masterGain);

      let mod: OscillatorNode | null = null;
      let modGain: GainNode | null = null;
      if (spec.fm) {
        mod = c.createOscillator();
        modGain = c.createGain();
        mod.frequency.setValueAtTime(spec.f * spec.fm.ratio, now);
        modGain.gain.setValueAtTime(spec.fm.index, now);
        mod.connect(modGain);
        modGain.connect(carrier.frequency);
        mod.start(now);
      }
      carrier.start(now);

      let stopped = false;
      return {
        setFrequency(hz: number): void {
          if (stopped || !ctx) return;
          // Smooth ramp so per-tick frequency changes don't click.
          const t = ctx.currentTime;
          carrier.frequency.setTargetAtTime(Math.max(1, hz), t, 0.05);
          if (mod && spec.fm) {
            mod.frequency.setTargetAtTime(Math.max(1, hz * spec.fm.ratio), t, 0.05);
          }
        },
        setGain(g: number): void {
          if (stopped || !ctx) return;
          voiceGain.gain.setTargetAtTime(Math.max(0, g), ctx.currentTime, 0.04);
        },
        stop(): void {
          if (stopped) return;
          stopped = true;
          if (!ctx) return;
          const t = ctx.currentTime;
          // Fast fade out so stop() doesn't click.
          voiceGain.gain.cancelScheduledValues(t);
          voiceGain.gain.setTargetAtTime(0, t, 0.02);
          // Schedule actual stop slightly after the fade ends.
          carrier.stop(t + 0.15);
          if (mod) mod.stop(t + 0.15);
        },
      };
    },

    destroy(): void {
      recipes.clear();
      if (ctx) {
        void ctx.close().catch(() => {
          /* ignore — best-effort teardown */
        });
        ctx = null;
        masterGain = null;
      }
    },
  };
}

function readMutedFromStorage(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMutedToStorage(muted: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore — storage may be disabled */
  }
}
